import {useEffect, useState, useCallback} from 'react';
import {api} from '../lib/auth.js';

/**
 * The reconcile surface between Jabr and Fortnox.
 *
 * Fortnox has a webhook for customers but none for articles, so a scheduled reconcile is
 * the only mechanism that holds for all three resources — and it is the right safety net
 * regardless, since a webhook missed once leaves the two copies apart forever while a
 * reconcile converges every time it runs.
 *
 * Four actions, kept apart rather than collapsed into "ändringar", because they carry
 * four different risks:
 *
 *   push    a local edit goes to Fortnox        (Jabr wins — this is the conflict rule)
 *   pull    a Fortnox edit comes here           (nothing leaves this machine)
 *   adopt   a record Jabr has never seen        (nothing leaves this machine)
 *   create  a record FORTNOX has never seen     (writes to the live accounting register)
 *
 * `create` is opt-in on its own button and is never part of "Synka". Adding 28 articles
 * to a live register is a different act from keeping two copies of an existing one in
 * step, and one button for both would make the safe operation carry the other's
 * consequences.
 */
const KINDS = {
	push: {label: 'Skickas till Fortnox', hint: 'lokal ändring som väntar — Jabr vinner'},
	pull: {label: 'Hämtas hit', hint: 'ändrat i Fortnox sedan förra körningen'},
	adopt: {label: 'Nya här', hint: 'finns i Fortnox men saknas i Jabr'},
	create: {label: 'Skapas i Fortnox', hint: 'finns bara i Jabr — skriver till bokföringen'},
};
const SAFE = ['push', 'pull', 'adopt'];

const RESOURCE_LABEL = {customers: 'Kunder', suppliers: 'Leverantörer', articles: 'Artiklar'};

function Actions({actions, kind}) {
	const rows = actions.filter(a => a.type === kind);
	if (!rows.length) return null;
	return (
		<div className="sync__group">
			<div className="sync__gh">
				<b>{KINDS[kind].label}</b> <span className="stamp">{rows.length} st · {KINDS[kind].hint}</span>
			</div>
			<div className="table-wrap">
				<table style={{minWidth: 700}}>
					<thead><tr><th>Id</th><th>Namn</th><th>Fält</th></tr></thead>
					<tbody>
						{rows.slice(0, 60).map(a => (
							<tr key={a.id}>
								<td>{a.id}</td><td>{a.name}</td>
								<td className="sync__fields">
									{Object.entries(a.fields).map(([k, v]) => (
										<span key={k} className="sync__f">
											{k}: <b>{String(v)}</b>
											{a.was && k in a.was && <i> (var {String(a.was[k] ?? '—')})</i>}
										</span>
									))}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{rows.length > 60 && <p className="stamp">…och {rows.length - 60} till.</p>}
		</div>
	);
}

export default function Sync() {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(null);
	const [result, setResult] = useState(null);

	const load = useCallback(async () => {
		setBusy('load'); setResult(null);
		try {
			const r = await api('/api/sync');
			const j = await r.json();
			if (!r.ok) throw new Error(j.error ?? `API svarade ${r.status}`);
			setData(j); setError(null);
		} catch (e) { setError(e.message); } finally { setBusy(null); }
	}, []);
	useEffect(() => { load(); }, [load]);

	const execute = async (resource, types) => {
		setBusy(resource + types.join()); setResult(null);
		try {
			const r = await api(`/api/sync/${resource}`, {
				method: 'POST', headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({types}),
			});
			const j = await r.json();
			setResult({resource, ...j, error: r.ok ? null : j.error});
		} catch (e) {
			setResult({resource, error: e.message});
		} finally { setBusy(null); await load(); }
	};

	if (error) return <div className="state">Kunde inte läsa synkläget: {error}</div>;
	if (!data) return <div className="state">Jämför med Fortnox…</div>;

	return (
		<>
			<p className="sub">
				Kunder, leverantörer och artiklar mot Fortnox-registret. Det här är en
				torrkörning — ingenting skrivs förrän du trycker på en knapp, och «Skapa i
				Fortnox» sitter för sig själv eftersom det är den enda åtgärden som lägger till
				något i den riktiga bokföringen.
			</p>

			{result && (
				<div className="panel">
					<div className="panel__title">
						{RESOURCE_LABEL[result.resource]}: {result.error ? 'misslyckades' : `${result.done?.length ?? 0} av ${result.attempted} klara`}
					</div>
					<p>
						{result.error ?? (result.failed?.length
							? `${result.failed.length} misslyckades: ${result.failed.map(f => `${f.id} (${f.error})`).join(', ')}`
							: 'Allt gick igenom.')}
						{result.failed?.length > 0 && ' Vattenmärket flyttades inte, så nästa körning tar om dem.'}
					</p>
				</div>
			)}

			<div className="toolbar">
				<button className="btn" onClick={load} disabled={!!busy}>
					{busy === 'load' ? 'Jämför…' : 'Uppdatera jämförelsen'}
				</button>
			</div>

			{Object.entries(data).map(([resource, p]) => {
				if (p.error) {
					return (
						<div className="sync__res" key={resource}>
							<h2 className="sim__name">{RESOURCE_LABEL[resource] ?? resource}</h2>
							<div className="state">{p.error}</div>
						</div>
					);
				}
				const safe = SAFE.reduce((n, t) => n + (p.summary[t] ?? 0), 0);
				return (
					<div className="sync__res" key={resource}>
						<div className="sim__head">
							<span className="sim__name">{RESOURCE_LABEL[resource] ?? resource}</span>
							<span className="stamp">
								{p.counts.fortnox} i Fortnox · {p.counts.jabr} i Jabr ·
								{' '}{p.last_sync ? `senast synkad ${new Date(p.last_sync).toLocaleString('sv-SE')}` : 'aldrig synkad'}
							</span>
							<span className="spacer" />
							<button className="btn" disabled={!!busy || !safe}
								onClick={() => execute(resource, SAFE)}>
								{safe ? `Synka ${safe}` : 'Inget att synka'}
							</button>
							{p.summary.create > 0 && (
								<button className="btn btn--primary" disabled={!!busy}
									onClick={() => execute(resource, ['create'])}
									title="Lägger till poster i det riktiga Fortnox-registret">
									Skapa {p.summary.create} i Fortnox
								</button>
							)}
						</div>
						{!p.actions.length && <p className="stamp">I takt med Fortnox.</p>}
						{['push', 'pull', 'adopt', 'create'].map(k =>
							<Actions key={k} actions={p.actions} kind={k} />)}
					</div>
				);
			})}

			<p className="foot-note">
				Konfliktregeln är att Jabr vinner — men bara där Jabr faktiskt har en oskickad
				ändring. «Jabr vinner alltid» hade skrivit tillbaka varje fält vid varje körning
				och permanent rullat tillbaka allt som redigerats direkt i Fortnox. Radering är
				avaktivering: Fortnox vägrar radera poster som en faktura hänvisar till, och en
				hård radering hade dessutom lämnat klassificeringsraderna hängande mot ett
				artikelnummer som inte finns. Verifikationer och fakturor rörs aldrig härifrån.
			</p>
		</>
	);
}
